import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Event } from '../types/event';
import { organizationService, eventService } from '../services/api';
import styles from './OrganizationPage.module.css';
import EventCard from '../components/EventCard';
import OrganizationEventDetail from '../components/OrganizationEventDetail';
import { 
  IconSearch, 
  IconChartBar, 
  IconCalendarEvent,
  IconMapPin,
  IconMail,
  IconPhone,
  IconWorld,
  IconFilter,
  IconArrowsSort,
  IconInfoCircle,
  IconLoader,
  IconPencil
} from '@tabler/icons-react';
import { ROUTES } from '../constants/routes';

const OrganizationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<any>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'popularity'>('date');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchOrganizationAndEvents = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const orgId = parseInt(id);
        
        // Fetch organization details
        const orgData = await organizationService.getById(orgId);
        setOrganization(orgData);
        
        // Fetch events for this organization directly using the dedicated endpoint
        const organizationEvents = await organizationService.getEvents(orgId);
        setEvents(organizationEvents);
      } catch (err) {
        console.error('Error fetching organization data:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationAndEvents();
  }, [id]);

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const handleCloseEventDetail = () => {
    setSelectedEvent(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSortChange = (sortType: 'date' | 'popularity') => {
    setSortBy(sortType);
    setShowFilters(false);
  };
  
  const handleTogglePastEvents = () => {
    setShowPastEvents(!showPastEvents);
    setShowFilters(false);
  };

  const handleInterestClick = async (eventId: number) => {
    try {
      await eventService.toggleInterest(eventId);
      
      // Update only the isInterested flag without modifying counts
      setEvents(events.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            isInterested: !event.isInterested
          } as Event;
        }
        return event;
      }));
    } catch (err) {
      console.error('Error toggling interest:', err);
    }
  };

  const handleSaveClick = async (eventId: number) => {
    try {
      await eventService.toggleSave(eventId);
      
      // Update only the isSaved flag without modifying counts
      setEvents(events.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            isSaved: !event.isSaved
          } as Event;
        }
        return event;
      }));
    } catch (err) {
      console.error('Error toggling save:', err);
    }
  };

  // Fonction pour rediriger vers la page d'édition
  const handleEditOrganization = () => {
    navigate(ROUTES.ORGANIZATION_EDIT.replace(':id', id || ''));
  };

  // Filter and sort events
  let filteredEvents = events.filter(event => {
    const now = new Date();
    const eventDate = new Date(event.dateTime);
    const isPast = eventDate < now;
    
    // Filter by search term
    const matchesSearch = 
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      event.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by event date (past/upcoming)
    const matchesDateFilter = showPastEvents || !isPast;
    
    return matchesSearch && matchesDateFilter;
  });
  
  // Sort events
  filteredEvents = [...filteredEvents].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime();
    } else {
      const aPopularity = (a._count?.interests || 0) + (a._count?.savedBy || 0) + (a._count?.views || 0);
      const bPopularity = (b._count?.interests || 0) + (b._count?.savedBy || 0) + (b._count?.views || 0);
      return bPopularity - aPopularity;
    }
  });

  // Stats calculation
  const totalEvents = events.length;
  const upcomingEvents = events.filter(event => new Date(event.dateTime) >= new Date()).length;
  // Calculé mais utilisé dans l'interface seulement pour référence future
  // const pastEvents = totalEvents - upcomingEvents;
  const totalViews = events.reduce((sum, event) => sum + (event._count?.views || 0), 0);
  const totalInterests = events.reduce((sum, event) => sum + (event._count?.interests || 0), 0);

  if (loading) {
    return (
      <div className={styles.loading}>
        <IconLoader size={40} className={styles.loadingIcon} />
        <p>Chargement des données de l'organisation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <IconInfoCircle size={30} />
        <p>Erreur: {error}</p>
        <button 
          onClick={() => window.location.reload()}
          className={styles.retryButton}
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!organization) {
    return <div className={styles.error}>Organisation non trouvée</div>;
  }

  return (
    <div className={styles.organizationPage}>
      <div className={styles.header}>
        <div className={styles.organizationHeader}>
          {organization.logoUrl ? (
            <img 
              src={organization.logoUrl} 
              alt={organization.name} 
              className={styles.logo} 
            />
          ) : (
            <div className={styles.logoPlaceholder}>
              {organization.name.charAt(0)}
            </div>
          )}
          <div className={styles.organizationInfo}>
            <div className={styles.organizationTitle}>
              <h1>{organization.name}</h1>
              <div className={styles.organizationActions}>
                <button 
                  className={styles.editButton}
                  onClick={handleEditOrganization}
                  title="Modifier le profil de l'organisation"
                >
                  <IconPencil size={18} />
                  Modifier
                </button>
                <button 
                  className={styles.createEventButton}
                  onClick={() => navigate(`${ROUTES.EVENT_NEW}?orgId=${organization.id}`)}
                  title="Créer un événement pour cette organisation"
                >
                  <IconCalendarEvent size={18} />
                  Créer un événement
                </button>
              </div>
            </div>
            {organization.description && (
              <p className={styles.description}>{organization.description}</p>
            )}
            <div className={styles.organizationStats}>
              <div className={styles.statItem}>
                <IconCalendarEvent size={18} />
                <span>{totalEvents} événements ({upcomingEvents} à venir)</span>
              </div>
              {totalViews > 0 && (
                <div className={styles.statItem}>
                  <IconChartBar size={18} />
                  <span>{totalViews} vues</span>
                </div>
              )}
              {totalInterests > 0 && (
                <div className={styles.statItem}>
                  <IconSearch size={18} />
                  <span>{totalInterests} intérêts</span>
                </div>
              )}
            </div>
            {organization.contactInfo && (
              <div className={styles.contactInfo}>
                {organization.contactInfo.address && (
                  <div className={styles.contactItem}>
                    <IconMapPin size={16} />
                    <span>{organization.contactInfo.address}</span>
                  </div>
                )}
                {organization.contactInfo.email && (
                  <div className={styles.contactItem}>
                    <IconMail size={16} />
                    <a href={`mailto:${organization.contactInfo.email}`}>{organization.contactInfo.email}</a>
                  </div>
                )}
                {organization.contactInfo.phone && (
                  <div className={styles.contactItem}>
                    <IconPhone size={16} />
                    <a href={`tel:${organization.contactInfo.phone}`}>{organization.contactInfo.phone}</a>
                  </div>
                )}
                {organization.contactInfo.website && (
                  <div className={styles.contactItem}>
                    <IconWorld size={16} />
                    <a href={organization.contactInfo.website} target="_blank" rel="noopener noreferrer">
                      {organization.contactInfo.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.eventsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <IconCalendarEvent size={20} />
              Événements
            </h2>
            <div className={styles.searchFilterContainer}>
              <div className={styles.searchBar}>
                <IconSearch size={18} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Rechercher un événement..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={styles.searchInput}
                />
              </div>
              <div className={styles.filterContainer}>
                <button 
                  className={styles.filterButton}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <IconFilter size={18} />
                  Filtrer
                </button>
                {showFilters && (
                  <div className={styles.filterDropdown}>
                    <div className={styles.filterGroup}>
                      <h3>Trier par</h3>
                      <button 
                        className={`${styles.filterOption} ${sortBy === 'date' ? styles.active : ''}`}
                        onClick={() => handleSortChange('date')}
                      >
                        <IconArrowsSort size={16} />
                        Date (plus récent)
                      </button>
                      <button 
                        className={`${styles.filterOption} ${sortBy === 'popularity' ? styles.active : ''}`}
                        onClick={() => handleSortChange('popularity')}
                      >
                        <IconChartBar size={16} />
                        Popularité
                      </button>
                    </div>
                    <div className={styles.filterGroup}>
                      <h3>Afficher</h3>
                      <button 
                        className={`${styles.filterOption} ${!showPastEvents ? styles.active : ''}`}
                        onClick={handleTogglePastEvents}
                      >
                        {!showPastEvents ? 'Uniquement à venir' : 'Inclure les passés'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {filteredEvents.length === 0 ? (
            <div className={styles.noEvents}>
              {searchTerm ? 'Aucun événement ne correspond à votre recherche.' : 'Aucun événement disponible.'}
              <div className={styles.noEventsActions}>
                <button 
                  className={styles.clearFiltersButton}
                  onClick={() => {
                    setSearchTerm('');
                    setShowPastEvents(true);
                    setSortBy('date');
                  }}
                >
                  Effacer les filtres
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.eventsGrid}>
              {filteredEvents.map(event => (
                <div 
                  key={event.id} 
                  className={styles.eventCardWrapper}
                  onClick={() => handleEventClick(event)}
                >
                  <EventCard 
                    event={event} 
                    onInterestClick={() => handleInterestClick(event.id)} 
                    onSaveClick={() => handleSaveClick(event.id)}
                    showStatus={true}
                  />
                  <div className={styles.viewStats}>
                    <IconChartBar size={16} />
                    <span>{event._count?.views || 0} vues</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {filteredEvents.length > 0 && (
            <div className={styles.pagination}>
              <span className={styles.resultsCount}>
                Affichage de {filteredEvents.length} événement{filteredEvents.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {selectedEvent && (
          <div className={styles.eventDetailWrapper}>
            <div className={styles.eventDetailContainer}>
              <OrganizationEventDetail 
                event={selectedEvent} 
                onClose={handleCloseEventDetail} 
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationPage;
